import { z } from "zod";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const USERNAME_PREFIX_PATTERN = /^[A-Za-z][A-Za-z0-9_]*$/;

export type ProfileSearchKind = "profileId" | "usernamePrefix";

export function profileSearchKind(q: string): ProfileSearchKind {
  return UUID_PATTERN.test(q) ? "profileId" : "usernamePrefix";
}

export const ProfileSearchQuery = z.strictObject({
  q: z
    .string()
    .trim()
    .min(2)
    .max(64)
    .refine(value => UUID_PATTERN.test(value) || USERNAME_PREFIX_PATTERN.test(value), {
      message: "q must be a profile id or the start of a username",
    })
    .meta({
      description:
        "A full profile id for an exact match, or the start of a username. Username text is matched case-insensitively against claimed usernames.",
      example: "gra",
    }),
  limit: z.coerce.number().int().min(1).max(10).default(10).meta({
    description: "Maximum results, 1–10. Defaults to 10.",
    example: 10,
  }),
});
export type ProfileSearchQuery = z.infer<typeof ProfileSearchQuery>;

export type PlayerSearchKind = "userId" | "email" | "usernamePrefix";

export function playerSearchKind(q: string): PlayerSearchKind {
  if (UUID_PATTERN.test(q)) return "userId";
  return q.includes("@") ? "email" : "usernamePrefix";
}

export const PlayerSearchQuery = z.strictObject({
  q: z
    .string()
    .trim()
    .min(2)
    .max(254)
    .refine(
      value => playerSearchKind(value) !== "usernamePrefix" || USERNAME_PREFIX_PATTERN.test(value),
      {
        message: "q must be a user id, the start of an email, or the start of a username",
      },
    )
    .meta({
      description:
        "A full user id for an exact match, text with an @ for the start of an email, or the start of a username. Email and username text are matched case-insensitively.",
      example: "ada@",
    }),
  limit: ProfileSearchQuery.shape.limit,
});
export type PlayerSearchQuery = z.infer<typeof PlayerSearchQuery>;
